import { subMonths } from 'date-fns/subMonths';
import {
  endOfDay,
  startOfDay,
  formatDateQuery,
  APP_MIN_DATE,
} from 'uplisting-frontend/utils/date';

export interface IAirbnbQualityDashboardDateRange {
  startDate: Date;
  endDate: Date;
  startDateStr: string;
  endDateStr: string;
  cacheKey: string;
}

/**
 * Computes a date range based on lookback months for Airbnb Quality Dashboard.
 * @param lookbackMonths - Number of months to look back (0 means all time)
 * @returns Date range with formatted strings and cache key
 */
export function computeDateRange(
  lookbackMonths: number,
): IAirbnbQualityDashboardDateRange {
  const now = new Date();
  const endDate = endOfDay(now);
  const startDate = startOfDay(
    lookbackMonths > 0 ? subMonths(now, lookbackMonths) : APP_MIN_DATE,
  );

  const startDateStr = formatDateQuery(startDate);
  const endDateStr = formatDateQuery(endDate);
  const cacheKey = `${startDateStr}-${endDateStr}`;

  return {
    startDate,
    endDate,
    startDateStr,
    endDateStr,
    cacheKey,
  };
}

export function extractNumericValue(value: string): number {
  if (!value) {
    return 0;
  }
  const numericValue = parseFloat(value.replace(/[^\d.-]/g, ''));
  return isNaN(numericValue) ? 0 : numericValue;
}

export type PolicyCategory =
  | 'flexible'
  | 'moderate'
  | 'firm'
  | 'strict'
  | 'super_strict_30'
  | 'super_strict_60'
  | 'non_refundable'
  | 'limited'
  | 'unknown';

export type QualityStatusCategory =
  | 'SUSPENDED'
  | 'SUSPEND'
  | 'GOOD'
  | 'EDUCATE'
  | 'WARN'
  | 'PROBATION'
  | 'ADDITIONAL_WARN'
  | 'PENDING_REMOVAL'
  | 'REMOVED'
  | 'UNKNOWN';

export function getPolicyValuesForCategory(category: PolicyCategory): string[] {
  switch (category) {
    case 'flexible':
      return ['flexible', 'flexible_new'];
    case 'moderate':
      return [
        'moderate',
        'moderate_new',
        'luxury_moderate',
        'luxury_moderate_14',
      ];
    case 'strict':
      return [
        'strict',
        'strict_new',
        'strict_14_with_grace_period',
        'luxury_super_strict_95',
        'luxury_super_strict_125',
      ];
    case 'firm':
      return ['firm_14', 'better_strict_with_grace_period'];
    case 'super_strict_30':
      return ['super_strict_30', 'super_strict_30_new'];
    case 'super_strict_60':
      return ['super_strict_60', 'super_strict_60_new'];
    case 'non_refundable':
      return [
        'non_refundable',
        'non-refundable',
        'no_refund',
        'luxury_no_refund',
      ];
    case 'limited':
      return ['limited'];
    default:
      return [];
  }
}

export function getQualityStatusValuesForCategory(
  category: QualityStatusCategory,
): string[] {
  const status = (category || 'UNKNOWN').toUpperCase();
  return status === 'SUSPENDED' ? ['SUSPEND', 'SUSPENDED'] : [status];
}
